import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import {
  getMyWorkOrdersApi,
  startWorkOrderApi,
  completeWorkOrderApi,
  pauseWorkOrderApi,
  resumeWorkOrderApi,
  type WorkOrderDetail,
} from '@/api/workorder.api';
import { getAllDowntimeCodes, type DowntimeCodeResponse } from '@/api/downtimecode.api';
import { StatusPill, WorkOrderFacts, formatDateTime } from '../work-orders/components/WorkOrderDetailDrawer';

// The operator's own work orders (the operator linked to the logged-in user): what is assigned,
// running or waiting for QC. The operator starts the work, records production, pauses it (with a
// reason: it is logged as the machine's downtime) and resumes it, and completes it; the assignment
// (crew, machine) is the production manager's.

export default function MyWorkOrdersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [operator, setOperator] = useState<{ id: string; name: string; employee_code: string | null } | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrderDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [pauseFor, setPauseFor] = useState<WorkOrderDetail | null>(null);
  const [pauseCodes, setPauseCodes] = useState<DowntimeCodeResponse[]>([]);
  const [pauseCodeId, setPauseCodeId] = useState('');
  const [pauseNotes, setPauseNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyWorkOrdersApi();
      setOperator(res.data?.operator || null);
      setWorkOrders(res.data?.workOrders || []);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not load your work orders');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const act = async (wo: WorkOrderDetail, action: 'start' | 'complete' | 'pause' | 'resume') => {
    setBusyId(wo.id);
    try {
      const res = action === 'start'
        ? await startWorkOrderApi(wo.id, { start_time: new Date().toISOString() })
        : action === 'complete'
          ? await completeWorkOrderApi(wo.id, {})
          : action === 'pause'
            ? await pauseWorkOrderApi(wo.id, { downtime_code_id: pauseCodeId, notes: pauseNotes.trim() || undefined })
            : await resumeWorkOrderApi(wo.id);
      if (res.success) {
        const done = { start: 'started', complete: 'completed', pause: 'paused', resume: 'resumed' }[action];
        toast.success(res.message || `${wo.wo_number} ${done}`);
        if (action === 'pause') setPauseFor(null);
      } else {
        toast.error(res.message || 'Could not update the work order');
      }
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not update the work order');
    } finally {
      setBusyId(null);
    }
  };

  // Pause reasons are the downtime codes of the work order's warehouse (and the common ones)
  const openPause = async (wo: WorkOrderDetail) => {
    setPauseFor(wo);
    setPauseCodeId('');
    setPauseNotes('');
    try {
      const res = await getAllDowntimeCodes();
      setPauseCodes((res.data || []).filter((c) =>
        c.is_active !== false && (!c.warehouse_id || !wo.warehouse_id || String(c.warehouse_id) === String(wo.warehouse_id))));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not load the pause reasons');
    }
  };

  const here = '/manufacturing/my-work-orders';

  return (
    <AppLayout>
      <div className="p-6 space-y-5 w-full max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">My Work Orders</h1>
            <p className="text-sm text-[#64748b] mt-0.5">
              {operator ? `${operator.name}${operator.employee_code ? ` · ${operator.employee_code}` : ''}` : 'Work assigned to you'}
            </p>
          </div>
          <button type="button" onClick={() => void load()}
            className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#475569] hover:bg-white cursor-pointer flex items-center gap-1.5">
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-[#94a3b8]"><i className="ri-loader-4-line animate-spin" /> Loading…</p>
        ) : !operator ? (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-8 text-center">
            <i className="ri-user-unfollow-line text-4xl text-[#cbd5e1] block mb-2" />
            <p className="text-sm font-medium text-[#475569]">Your login is not linked to an operator</p>
            <p className="text-xs text-[#94a3b8] mt-1">Ask an admin to link it in Masters → Operators (Login User).</p>
          </div>
        ) : workOrders.length === 0 ? (
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-8 text-center">
            <i className="ri-checkbox-circle-line text-4xl text-emerald-400 block mb-2" />
            <p className="text-sm font-medium text-[#475569]">No work assigned to you right now</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workOrders.map((wo) => {
              const isOpen = openId === wo.id;
              const busy = busyId === wo.id;
              const due = wo.planned_end_at || wo.planned_end_date;
              return (
                <div key={wo.id} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                  <div className="flex items-start justify-between gap-3 p-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-[#1e293b]">{wo.wo_number}</span>
                        <span className="text-[#475569]">— {wo.stage_name}</span>
                        <StatusPill status={wo.display_status} />
                      </div>
                      <p className="text-sm text-[#64748b] mt-1">
                        {wo.po_number} · {wo.item_name} · Stage {wo.sequence.current} of {wo.sequence.total}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-[#475569] flex-wrap">
                        <span><i className="ri-settings-3-line text-[#94a3b8]" /> {wo.machine_name || 'No machine'}</span>
                        <span><i className="ri-focus-3-line text-[#94a3b8]" /> Target {Number(wo.planned_qty)} {wo.unit}</span>
                        {Number(wo.completed_qty) > 0 && <span><i className="ri-check-line text-[#94a3b8]" /> Done {Number(wo.completed_qty)}</span>}
                        <span><i className="ri-time-line text-[#94a3b8]" /> Due {formatDateTime(due)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {wo.display_status === 'ASSIGNED' && (
                        <button type="button" disabled={busy} onClick={() => void act(wo, 'start')}
                          className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 cursor-pointer flex items-center gap-1.5">
                          {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-play-fill" />} Start
                        </button>
                      )}
                      {wo.display_status === 'PAUSED' && (
                        <button type="button" disabled={busy} onClick={() => void act(wo, 'resume')}
                          className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 cursor-pointer flex items-center gap-1.5">
                          {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-play-fill" />} Resume
                        </button>
                      )}
                      {wo.status === 'IN_PROGRESS' && (
                        <>
                          <button type="button" onClick={() => navigate(`/manufacturing/shop-floor?woId=${wo.id}&return_url=${encodeURIComponent(here)}`)}
                            className="h-9 px-3 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer flex items-center gap-1.5">
                            <i className="ri-add-circle-line" /> Production Entry
                          </button>
                          {wo.display_status !== 'PAUSED' && wo.machine_id && (
                            <button type="button" disabled={busy} onClick={() => void openPause(wo)}
                              className="h-9 px-3 rounded-lg border border-orange-200 text-orange-700 text-sm font-medium hover:bg-orange-50 disabled:opacity-50 cursor-pointer flex items-center gap-1.5">
                              <i className="ri-pause-line" /> Pause
                            </button>
                          )}
                          <button type="button" disabled={busy} onClick={() => void act(wo, 'complete')}
                            className="h-9 px-3 rounded-lg border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50 cursor-pointer flex items-center gap-1.5">
                            {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-double-line" />} Complete
                          </button>
                        </>
                      )}
                      {['QC_PENDING', 'COMPLETED_QC_PENDING'].includes(wo.status) && (
                        <span className="text-xs font-medium text-purple-700"><i className="ri-microscope-line" /> Waiting for QC</span>
                      )}
                      {wo.status === 'QC_FAILED' && (
                        <span className="text-xs font-medium text-rose-700"><i className="ri-close-circle-line" /> QC failed — rework follows</span>
                      )}
                      <button type="button" onClick={() => setOpenId(isOpen ? null : wo.id)}
                        className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] text-[#64748b] hover:bg-slate-50 cursor-pointer"
                        title={isOpen ? 'Hide details' : 'Show details'}>
                        <i className={isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                      </button>
                    </div>
                  </div>
                  {pauseFor?.id === wo.id && (
                    <div className="border-t border-orange-100 bg-orange-50/50 px-4 py-3 flex items-end gap-3 flex-wrap">
                      <label className="flex flex-col gap-1 text-xs font-medium text-[#475569]">
                        Reason
                        <select value={pauseCodeId} onChange={(e) => setPauseCodeId(e.target.value)}
                          className="h-9 min-w-[220px] rounded-lg border border-[#e2e8f0] bg-white px-2 text-sm text-[#1e293b]">
                          <option value="">Select reason…</option>
                          {pauseCodes.map((c) => (
                            <option key={c.id} value={c.id}>{c.code} — {c.description} ({c.category})</option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col gap-1 text-xs font-medium text-[#475569] flex-1 min-w-[180px]">
                        Notes
                        <input value={pauseNotes} onChange={(e) => setPauseNotes(e.target.value)} placeholder="Optional"
                          className="h-9 rounded-lg border border-[#e2e8f0] bg-white px-2 text-sm text-[#1e293b]" />
                      </label>
                      <button type="button" disabled={busy || !pauseCodeId} onClick={() => void act(wo, 'pause')}
                        className="h-9 px-4 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 disabled:opacity-50 cursor-pointer flex items-center gap-1.5">
                        {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-pause-fill" />} Pause
                      </button>
                      <button type="button" onClick={() => setPauseFor(null)}
                        className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#475569] hover:bg-white cursor-pointer">
                        Cancel
                      </button>
                      {pauseCodes.length === 0 && (
                        <p className="w-full text-xs text-[#94a3b8]">No downtime codes yet — add them in Masters → Downtime Codes.</p>
                      )}
                    </div>
                  )}
                  {isOpen && (
                    <div className="border-t border-[#f1f5f9] px-4 py-3 space-y-4 bg-[#f8fafc]">
                      <WorkOrderFacts wo={wo} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
